import {
  Divider,
  Group,
  Image,
  Loader,
  Stack,
  Tabs,
  Text,
  Tooltip,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { Activity, LayoutGrid, Ruler } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PageHeader from '@components/shared/PageHeader';
import { useAuthUser } from '@store/useAuthStore';
import type { DeviceConfigurationApi } from '@model/DeviceConfiguration';
import { useUserConfigurationsQuery } from '../../../hooks/queries/useUserConfigurationsQuery';
import { useUserVideosQuery } from '../../../hooks/queries/useUserVideosQuery';
import { DeletePatientDialog } from '../components/DeletePatientDialog';
import { PatientBaseProfileCard } from '../components/PatientBaseProfileCard';
import { PatientBodyDomainTab } from '../components/PatientBodyDomainTab';
import { PatientOverviewTab } from '../components/PatientOverviewTab';
import { PatientSessionsTab } from '../components/PatientSessionsTab';
import {
  useCreatePatientMeasurementMutation,
  useDeletePatientMeasurementMutation,
  useDeletePatientMutation,
  usePatientMeasurementsHistoryQuery,
  usePatientQuery,
  useUpdatePatientBaseProfileMutation,
} from '../hooks/usePatientsQueries';
import { createMeasurementTemplate } from '../utils/measurements';
import {
  buildNutritionInput,
  calculatePatientNutritionMetrics,
} from '../utils/nutrition';
import { EMPTY_PATIENT_BASE_PROFILE } from '../utils/profile';
import {
  summarizePatientSessions,
  toPatientSessionList,
} from '../utils/sessions';
import { USER_ROUTES } from '../../../routes/userRoutes';
import { getProductIconUrl } from '../../../types/contants';

const DEFAULT_TAB = 'overview';

function UserDetailPageHeader({
  breadcrumbLabel,
}: {
  breadcrumbLabel: string;
}) {
  const { t } = useTranslation();

  return (
    <>
      <PageHeader
        title=''
        breadcrumbs={[
          {
            label: t('left_bar_users', 'Utenti'),
            href: USER_ROUTES.list,
          },
          { label: breadcrumbLabel },
        ]}
        backUrl={USER_ROUTES.list}
      />
      <Divider mt='md' mb='lg' />
    </>
  );
}

export function PatientDetailPage() {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const params = useParams<{ userId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const patientId = Number(params.userId ?? 0);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const currentUser = useAuthUser();
  const patientQuery = usePatientQuery(
    Number.isFinite(patientId) && patientId > 0 ? patientId : null
  );
  const measurementsQuery = usePatientMeasurementsHistoryQuery(
    Number.isFinite(patientId) && patientId > 0 ? patientId : null
  );
  const updateProfileMutation = useUpdatePatientBaseProfileMutation(
    Number.isFinite(patientId) && patientId > 0 ? patientId : 0
  );
  const createMeasurementMutation = useCreatePatientMeasurementMutation(
    Number.isFinite(patientId) && patientId > 0 ? patientId : 0
  );
  const deleteMeasurementMutation = useDeletePatientMeasurementMutation(
    Number.isFinite(patientId) && patientId > 0 ? patientId : 0
  );
  const deleteMutation = useDeletePatientMutation();
  const sessionsQuery = useUserConfigurationsQuery(currentUser?.id?.toString());
  const videosQuery = useUserVideosQuery();

  const activeTab = searchParams.get('tab') ?? DEFAULT_TAB;
  const isNewMeasurementMode = searchParams.get('mode') === 'new-measurement';
  const patient = patientQuery.data ?? null;
  const history = measurementsQuery.data ?? [];
  const latestMeasurement = history[0] ?? null;
  const profile = patient?.profile ?? EMPTY_PATIENT_BASE_PROFILE;

  const newMeasurementTemplate = useMemo(
    () => createMeasurementTemplate(patientId, latestMeasurement, profile),
    [latestMeasurement, patientId, profile]
  );

  const nutritionInput = useMemo(
    () => buildNutritionInput(profile, latestMeasurement),
    [latestMeasurement, profile]
  );
  const metrics = useMemo(
    () => calculatePatientNutritionMetrics(nutritionInput),
    [nutritionInput]
  );

  const patientSessions = useMemo(() => {
    if (patient == null) {
      return [];
    }

    const rawSessions = (sessionsQuery.data ?? []) as DeviceConfigurationApi[];
    return toPatientSessionList(
      rawSessions,
      patient.id,
      videosQuery.data ?? []
    );
  }, [patient, sessionsQuery.data, videosQuery.data]);

  const sessionsSummary = useMemo(
    () => summarizePatientSessions(patientSessions),
    [patientSessions]
  );

  const handleDelete = async () => {
    if (!patient) return;
    await deleteMutation.mutateAsync(patient.id);
    navigate(USER_ROUTES.list);
  };

  if (patientQuery.isPending || measurementsQuery.isPending) {
    return (
      <>
        <UserDetailPageHeader breadcrumbLabel='Caricamento' />
        <Loader />
      </>
    );
  }

  if (patientQuery.isError || patient == null) {
    return (
      <>
        <UserDetailPageHeader breadcrumbLabel='Errore' />
        <Text c='red'>
          {patientQuery.error instanceof Error
            ? patientQuery.error.message
            : 'Utente non disponibile'}
        </Text>
      </>
    );
  }

  const updateSearchParams = (updates: Record<string, string | null>) => {
    const nextParams = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([key, value]) => {
      if (value == null || value === '') {
        nextParams.delete(key);
      } else {
        nextParams.set(key, value);
      }
    });

    setSearchParams(nextParams, { replace: true });
  };

  const handleTabChange = (value: string | null) => {
    updateSearchParams({
      tab: value ?? DEFAULT_TAB,
      mode: value === 'body' && isNewMeasurementMode ? 'new-measurement' : null,
    });
  };

  const openBodyDomain = (withForm: boolean = false) => {
    updateSearchParams({
      tab: 'body',
      mode: withForm ? 'new-measurement' : null,
    });
  };

  const openSessionsDomain = () => {
    updateSearchParams({
      tab: 'sessions',
      mode: null,
    });
  };

  return (
    <>
      <UserDetailPageHeader
        breadcrumbLabel={
          isMobile ? patient.code : `${patient.code} · ID #${patient.id}`
        }
      />

      <Stack gap='lg'>
        {!isNewMeasurementMode && (
          <PatientBaseProfileCard
            userCode={patient.code}
            profile={profile}
            saving={updateProfileMutation.isPending}
            onSubmit={async values => {
              await updateProfileMutation.mutateAsync(values);
            }}
          />
        )}

        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tabs.List>
            <Tabs.Tab value='overview' leftSection={<LayoutGrid size={16} />}>
              Overview
            </Tabs.Tab>
            <Tooltip
              withArrow
              multiline
              w={280}
              label={
                <Group gap={6} wrap='nowrap' align='center'>
                  <Text size='xs'>Compatibile con</Text>
                  <Image
                    src={getProductIconUrl('exposure')}
                    w={16}
                    h={16}
                    fit='contain'
                    alt=''
                  />
                  <Text size='xs'>Augmented Exposure Body Image</Text>
                </Group>
              }
            >
              <Tabs.Tab value='body' leftSection={<Ruler size={16} />}>
                Misure corporee
              </Tabs.Tab>
            </Tooltip>
            <Tabs.Tab value='sessions' leftSection={<Activity size={16} />}>
              Sessioni
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value='overview' pt='md'>
            <PatientOverviewTab
              latestMeasurement={latestMeasurement}
              measurementsCount={history.length}
              metrics={metrics}
              sessionsSummary={sessionsSummary}
              onOpenBodyDomain={() => openBodyDomain(false)}
              onOpenSessionsDomain={openSessionsDomain}
            />
          </Tabs.Panel>

          <Tabs.Panel value='body' pt='md'>
            <PatientBodyDomainTab
              patientId={patient.id}
              profile={profile}
              isFormOpen={isNewMeasurementMode}
              isSaving={createMeasurementMutation.isPending}
              isDeletingMeasurement={deleteMeasurementMutation.isPending}
              deletingMeasurementId={
                deleteMeasurementMutation.isPending
                  ? (deleteMeasurementMutation.variables ?? null)
                  : null
              }
              initialValues={newMeasurementTemplate}
              onOpenForm={() => openBodyDomain(true)}
              onCloseForm={() => openBodyDomain(false)}
              onSubmit={async values => {
                await createMeasurementMutation.mutateAsync(values);
                openBodyDomain(false);
              }}
              onDeleteMeasurement={async measurementId => {
                await deleteMeasurementMutation.mutateAsync(measurementId);
              }}
            />
          </Tabs.Panel>

          <Tabs.Panel value='sessions' pt='md'>
            <PatientSessionsTab
              sessions={patientSessions}
              summary={sessionsSummary}
            />
          </Tabs.Panel>
        </Tabs>
      </Stack>

      <DeletePatientDialog
        patient={patient}
        opened={deleteOpen}
        loading={deleteMutation.isPending}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
      />
    </>
  );
}
